import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Spinner, Alert, Badge, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { fetchLivreurTimeline, assignLivreur } from "./DataService";
import { GetOneCommandeById } from "../Fournisseur/CommandesFournisseurs/DataService";
import BadgeEstimationManutention from "../../components/BadgeEstimationManutention";
import { libelleAscenseur } from "../../utils/libelleAscenseur";

const STATUS_INFO = {
  available:   { icon: "🟢", label: "Disponible",  bg: "success" },
  busy:        { icon: "🟡", label: "Occupé",       bg: "warning" },
  overloaded:  { icon: "🔴", label: "Surchargé",     bg: "danger" },
  unavailable: { icon: "⚫", label: "Indisponible (congé/repos)", bg: "dark" },
};

const TYPE_LIVRAISON_BADGE = {
  urgent: { label: "Urgent", bg: "danger" },
  planifiee: { label: "Planifiée", bg: "primary" },
  flex: { label: "Flex", bg: "secondary" },
};

const STATUT_COMMANDE_BADGE = {
  1: { label: "En cours de livraison", bg: "warning" },
  2: { label: "Livré", bg: "success" },
  3: { label: "En préparation", bg: "info" },
  4: { label: "En cours de validation", bg: "primary" },
  5: { label: "Validé", bg: "secondary" },
  6: { label: "Annulé", bg: "dark" },
};

// Au-delà de ce seuil, l'écart entre deux commandes est mis en avant comme un vrai "trou"
// exploitable plutôt qu'un simple espacement normal entre deux livraisons.
const GAP_THRESHOLD_MINUTES = 45;

function formatHeure(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function gapMinutes(prevIso, nextIso) {
  if (!prevIso || !nextIso) return null;
  return Math.round((new Date(nextIso) - new Date(prevIso)) / 60000);
}

function formatDureeMinutes(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  if (h === 0) return `${m} min`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

// Somme des trajet_minutes/distance_km connus ; ignore les commandes sans valeur
// (adresse manquante, appel Directions échoué) plutôt que de les compter comme 0.
function sommeTrajets(commandes) {
  const minutes = commandes.map((c) => c.trajet_minutes).filter((v) => v != null);
  const km = commandes.map((c) => c.distance_km).filter((v) => v != null);
  return {
    minutes: minutes.length > 0 ? minutes.reduce((a, b) => a + b, 0) : null,
    km: km.length > 0 ? km.reduce((a, b) => a + b, 0) : null,
  };
}

function formatDateHeure(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function euros(value) {
  if (value === null || value === undefined || value === "") return "—";
  return `${value} €`;
}

// Carte commande, glissable — réutilisée dans le panneau "non assignées" et dans chaque
// colonne livreur (glisser une commande déjà assignée vers un autre livreur = réassignation).
function CommandeCard({ cmd, draggable, onOpenDetail }) {
  const typeBadge = TYPE_LIVRAISON_BADGE[(cmd.type_livraison || "").toLowerCase()];
  const statutBadge = STATUT_COMMANDE_BADGE[Number(cmd.statut_commande)];

  return (
    <div
      className="border rounded p-2 mb-2 bg-white"
      style={{ fontSize: 13, cursor: onOpenDetail ? "pointer" : (draggable ? "grab" : undefined) }}
      draggable={draggable}
      onDragStart={draggable ? (e) => e.dataTransfer.setData("text/plain", String(cmd.id)) : undefined}
      onClick={onOpenDetail ? () => onOpenDetail(cmd.id) : undefined}
    >
      <div className="d-flex justify-content-between align-items-start">
        <strong>{formatHeure(cmd.date_livraison)}</strong>
        <div className="d-flex gap-1">
          {cmd.manutention_deux_personnes && (
            <Badge bg="warning" text="dark" style={{ fontSize: 10 }} title="Manutention à 2 personnes">
              👥 2 pers.
            </Badge>
          )}
          {typeBadge && <Badge bg={typeBadge.bg} style={{ fontSize: 10 }}>{typeBadge.label}</Badge>}
        </div>
      </div>
      {(statutBadge || cmd.estimation_manutention) && (
        <div className="mt-1 d-flex flex-wrap gap-1">
          {statutBadge && <Badge bg={statutBadge.bg} style={{ fontSize: 10 }}>{statutBadge.label}</Badge>}
          <BadgeEstimationManutention estimation={cmd.estimation_manutention} />
        </div>
      )}
      <div className="text-muted" style={{ fontSize: 11 }}>#{cmd.id} — {cmd.client || "—"}</div>
      <div className="mt-1">
        <div>📍 {cmd.adresse_pickup}</div>
        <div>🎯 {cmd.adresse_livraison}</div>
      </div>
      <div className="d-flex gap-2 mt-1 text-muted" style={{ fontSize: 11 }}>
        {cmd.trajet_minutes != null && <span>🚗 {cmd.trajet_minutes} min de trajet</span>}
        {cmd.poids_label && <span>⚖️ {cmd.poids_label}</span>}
      </div>
      {cmd.suggestions && cmd.suggestions.length > 0 && (
        <div className="mt-1" style={{ fontSize: 11 }}>
          {cmd.suggestions.map((s) => (
            <div key={s.livreur_id} className="text-primary">
              💡 {s.name} — {s.minutes} min du ramassage
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LivreurCard({ livreur, onDropCommande, onOpenDetail }) {
  const [dragOver, setDragOver] = useState(false);
  const status = STATUS_INFO[livreur.status] ?? { icon: "⚪", label: livreur.status, bg: "secondary" };
  const { minutes: totalMinutes, km: totalKm } = sommeTrajets(livreur.commandes);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const commandeId = e.dataTransfer.getData("text/plain");
    if (commandeId) onDropCommande(commandeId, livreur.id);
  };

  return (
    <Card
      className="border-0 shadow-sm h-100"
      style={dragOver ? { outline: "2px dashed #0d6efd", outlineOffset: "-2px" } : undefined}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <Card.Header className="bg-white">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>{livreur.name}</strong>{" "}
            {livreur.is_online && <span title="En ligne">🟢</span>}
          </div>
          <Badge bg={status.bg}>{status.icon} {status.label}</Badge>
        </div>
        <div className="small text-muted mt-1">
          {livreur.deliveries_today}/{livreur.max_deliveries} livraisons aujourd'hui
        </div>
        {(totalMinutes != null || totalKm != null) && (
          <div className="small text-muted mt-1 d-flex gap-3">
            {totalMinutes != null && <span>🚗 {formatDureeMinutes(totalMinutes)} de trajet estimé</span>}
            {totalKm != null && <span>📏 {totalKm.toFixed(1)} km estimés</span>}
          </div>
        )}
      </Card.Header>
      <Card.Body style={{ maxHeight: "65vh", overflowY: "auto", minHeight: 120 }}>
        {livreur.commandes.length === 0 ? (
          <div className="text-muted small text-center py-4">
            Aucune livraison ce jour — glissez une commande ici
          </div>
        ) : (
          livreur.commandes.map((cmd, i) => {
            const prev = livreur.commandes[i - 1];
            const gap = prev ? gapMinutes(prev.date_livraison, cmd.date_livraison) : null;

            return (
              <div key={cmd.id}>
                {gap !== null && gap >= GAP_THRESHOLD_MINUTES && (
                  <div className="text-center my-2">
                    <Badge bg="light" text="dark" style={{ border: "1px dashed #ccc" }}>
                      ⏳ {gap} min disponible
                    </Badge>
                  </div>
                )}
                <CommandeCard cmd={cmd} draggable onOpenDetail={onOpenDetail} />
              </div>
            );
          })
        )}
      </Card.Body>
    </Card>
  );
}

function UnassignedPanel({ commandes, onOpenDetail }) {
  if (commandes.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm mb-3" style={{ borderLeft: "4px solid #dc3545" }}>
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <strong>⚠️ Non assignées</strong>
        <Badge bg="danger">{commandes.length}</Badge>
      </Card.Header>
      <Card.Body>
        <div className="text-muted small mb-2">Glissez une commande vers un livreur pour l'assigner.</div>
        <Row className="g-2">
          {commandes.map((cmd) => (
            <Col md={4} key={cmd.id}>
              <CommandeCard cmd={cmd} draggable onOpenDetail={onOpenDetail} />
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  );
}

function CommandeDetailModal({ commandeId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!commandeId) return;
    setDetail(null);
    setError(null);
    setLoading(true);
    GetOneCommandeById(commandeId)
      .then(setDetail)
      .catch(() => setError("Impossible de charger le détail de cette commande."))
      .finally(() => setLoading(false));
  }, [commandeId]);

  const typeBadge = detail && TYPE_LIVRAISON_BADGE[(detail.type_livraison || "").toLowerCase()];
  const hasManutention = detail && (detail.manutention_status || detail.manutention_total_ttc);
  const nomClient = detail ? `${detail.PrenomClient || ""} ${detail.NomClient || ""}`.trim() : "";

  return (
    <Modal show={!!commandeId} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Commande #{commandeId}{nomClient && ` — ${nomClient}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}

        {detail && !loading && (
          <>
            <Row className="mb-3 g-2 align-items-center">
              {typeBadge && <Col xs="auto"><Badge bg={typeBadge.bg}>{typeBadge.label}</Badge></Col>}
              <Col xs="auto" className="text-muted small">
                Client : <strong>{(detail.PrenomClient || "") + " " + (detail.NomClient || "")}</strong>
              </Col>
            </Row>

            <Card className="mb-3">
              <Card.Body>
                <Card.Title style={{ fontSize: 15 }}>🕐 Livraison</Card.Title>
                <Row className="g-2">
                  <Col md={6}><small className="text-muted d-block">Heure prévue</small>{formatDateHeure(detail.DateLivraison)}</Col>
                  <Col md={6}><small className="text-muted d-block">Heure réelle</small>{formatDateHeure(detail.DateLivraisonReelle)}</Col>
                  <Col md={6}><small className="text-muted d-block">Pickup</small>{detail.AdressePickUp || "—"}</Col>
                  <Col md={6}><small className="text-muted d-block">Livraison</small>{detail.AdresseLivraison || "—"}</Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <Card.Title style={{ fontSize: 15 }}>⚖️ Poids & tarification</Card.Title>
                <Row className="g-2">
                  <Col md={4}><small className="text-muted d-block">Poids</small>{detail.poids_label || detail.poids || "—"}</Col>
                  <Col md={4}><small className="text-muted d-block">Suppl. poids</small>{euros(detail.poids_supplement)}</Col>
                  <Col md={4}><small className="text-muted d-block">Temps estimé</small>{detail.temps_estime || "—"}</Col>
                  <Col md={4}><small className="text-muted d-block">Tarif base</small>{euros(detail.tarif_base)}</Col>
                  <Col md={4}><small className="text-muted d-block">Tarif HT</small>{euros(detail.tarif_ht)}</Col>
                  <Col md={4}><small className="text-muted d-block">Tarif TTC</small>{euros(detail.tarif_ttc)}</Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <Card.Title style={{ fontSize: 15 }}>📦 Manutention</Card.Title>
                {hasManutention ? (
                  <Row className="g-2">
                    <Col md={4}><small className="text-muted d-block">Étage</small>{detail.manutention_etage ?? "—"}</Col>
                    <Col md={4}><small className="text-muted d-block">Ascenseur</small>{libelleAscenseur(detail.manutention_ascenseur)}</Col>
                    <Col md={4}><small className="text-muted d-block">Marches estimées</small>{detail.manutention_marches_estimees ?? "—"}</Col>
                    <Col md={4}><small className="text-muted d-block">Distance de portage</small>{detail.manutention_distance ? `${detail.manutention_distance} m` : "—"}</Col>
                    <Col md={4}><small className="text-muted d-block">Obstacles</small>{detail.manutention_obstacles || "—"}</Col>
                    <Col md={4}><small className="text-muted d-block">Poids colis</small>{detail.manutention_poids_colis ?? "—"}</Col>
                    {detail.manutention_description_acces && (
                      <Col md={12}><small className="text-muted d-block">Description accès</small>{detail.manutention_description_acces}</Col>
                    )}
                    <Col md={6}><small className="text-muted d-block">Total manutention HT</small>{euros(detail.manutention_total_ht)}</Col>
                    <Col md={6}><small className="text-muted d-block">Total manutention TTC</small>{euros(detail.manutention_total_ttc)}</Col>
                  </Row>
                ) : (
                  <div className="text-muted small">Aucune manutention demandée sur cette commande.</div>
                )}
              </Card.Body>
            </Card>

            {detail.Commentaire && (
              <Card className="mb-3">
                <Card.Body>
                  <Card.Title style={{ fontSize: 15 }}>💬 Commentaire</Card.Title>
                  <div className="small">{detail.Commentaire}</div>
                </Card.Body>
              </Card>
            )}

            {detail.historique_statuts?.length > 0 && (
              <Card>
                <Card.Body>
                  <Card.Title style={{ fontSize: 15 }}>📜 Historique</Card.Title>
                  {detail.historique_statuts.map((h, i) => (
                    <div key={i} className="small text-muted d-flex justify-content-between border-bottom py-1">
                      <span>{h.statut}</span>
                      <span>{h.qui} — {h.quand}</span>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

function LivreurTimelineScreen() {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [data, setData] = useState({ livreurs: [], non_assignees: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCommandeId, setSelectedCommandeId] = useState(null);

  const load = () => {
    setLoading(true);
    return fetchLivreurTimeline(date)
      .then(setData)
      .catch(() => setError("Impossible de charger la timeline."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleDropCommande = async (commandeId, livreurId) => {
    const livreur = data.livreurs.find((l) => l.id === livreurId);
    if (livreur && livreur.deliveries_today >= livreur.max_deliveries) {
      const confirmed = window.confirm(
        `${livreur.name} est déjà à ${livreur.deliveries_today}/${livreur.max_deliveries} livraisons aujourd'hui. Assigner quand même ?`
      );
      if (!confirmed) return;
    }

    try {
      await assignLivreur(commandeId, livreurId);
      toast.success(`Commande #${commandeId} assignée`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur lors de l'assignation");
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Timeline par livreur</h4>
          <small className="text-muted">Disponibilité + livraisons du jour, par livreur</small>
        </Col>
        <Col xs="auto">
          <Form.Control type="date" size="sm" value={date} onChange={(e) => setDate(e.target.value)} />
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <UnassignedPanel commandes={data.non_assignees || []} onOpenDetail={setSelectedCommandeId} />
          <Row className="g-3">
            {data.livreurs.map((livreur) => (
              <Col md={4} key={livreur.id}>
                <LivreurCard livreur={livreur} onDropCommande={handleDropCommande} onOpenDetail={setSelectedCommandeId} />
              </Col>
            ))}
          </Row>
        </>
      )}

      <CommandeDetailModal commandeId={selectedCommandeId} onClose={() => setSelectedCommandeId(null)} />
    </Container>
  );
}

export default LivreurTimelineScreen;
